export interface AccessTokenState {
  accessToken: string;
  tokenType: "Bearer";
  expiresAt: number;
  issuedAt: number;
}

export interface AccessTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export class MemoryOnlyTokenManager {
  private tokenState: AccessTokenState | undefined;

  setToken(response: AccessTokenResponse, now = Date.now()): AccessTokenState {
    if (
      !response.access_token ||
      response.token_type.toLowerCase() !== "bearer" ||
      response.expires_in <= 0
    ) {
      throw new Error("Invalid access token response.");
    }

    this.tokenState = {
      accessToken: response.access_token,
      tokenType: "Bearer",
      issuedAt: now,
      expiresAt: now + response.expires_in * 1000,
    };

    return this.tokenState;
  }

  getToken(now = Date.now()): string | undefined {
    if (!this.tokenState || this.isExpired(now)) {
      return undefined;
    }

    return this.tokenState.accessToken;
  }

  getTokenState(): AccessTokenState | undefined {
    return this.tokenState ? { ...this.tokenState } : undefined;
  }

  clear(): void {
    this.tokenState = undefined;
  }

  isExpired(now = Date.now()): boolean {
    return !this.tokenState || now >= this.tokenState.expiresAt;
  }

  shouldRefresh(now = Date.now(), refreshThreshold = 0.8): boolean {
    if (!this.tokenState) {
      return true;
    }

    const lifetime = this.tokenState.expiresAt - this.tokenState.issuedAt;
    const elapsed = now - this.tokenState.issuedAt;
    return elapsed / lifetime >= refreshThreshold;
  }
}

export const accessTokenManager = new MemoryOnlyTokenManager();
